import './GymLogos.css';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getFirestore } from 'firebase/firestore';

import { useAuth } from '../../contexts/AuthContext';
import firebase from '../../firebase';
import {
  deleteGymLogo,
  fetchGymLogos,
  GYM_LOGO_ALLOWED_TYPES,
  GYM_LOGO_MAX_BYTES,
  MAX_GYM_LOGOS,
  updateGymLogoName,
  uploadGymLogo,
} from '../../services/gymLogoService';
import type { GymLogo } from '../../types/gymLogo';
import Button from '../common/Button';
import Card from '../common/Card';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import IconButton from '../common/IconButton/IconButton';
import InlineInputWithIntegratedCheckmark
  from '../common/InlineInputWithIntegratedCheckmark/InlineInputWithIntegratedCheckmark';
import PencilIcon from '../common/PencilIcon';
import TrashIcon from '../common/TrashIcon';

const GymLogos: React.FC = () => {
  const { currentUser } = useAuth();

  const [logos, setLogos] = useState<GymLogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingName, setPendingName] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const [deletePending, setDeletePending] = useState<GymLogo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [editingLogoId, setEditingLogoId] = useState<string | null>(null);
  const [draftLogoName, setDraftLogoName] = useState('');
  const [savingLogoName, setSavingLogoName] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track the blob URL in a ref so it can be revoked on unmount even if state is stale.
  const previewBlobRef = useRef<string | null>(null);

  const uid = currentUser?.uid ?? null;

  // Revoke the blob URL on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
      }
    };
  }, []);

  const loadSignal = useMemo(() => ({ uid }), [uid]);

  useEffect(() => {
    const db = getFirestore(firebase);
    if (!loadSignal.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setListError('');
    fetchGymLogos(db, loadSignal.uid)
      .then((items) => {
        if (!cancelled) setLogos(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setListError('Failed to load gym logos');
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadSignal]);

  const clearPendingUpload = (): void => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    setPendingFile(null);
    setPendingName('');
    setPreviewUrl(null);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const beginEditLogoName = (logo: GymLogo): void => {
    setEditingLogoId(logo.id);
    setDraftLogoName(logo.name);
  };

  const cancelEditLogoName = (): void => {
    setEditingLogoId(null);
    setDraftLogoName('');
  };

  const saveEditLogoName = async (logoId: string): Promise<void> => {
    const trimmed = draftLogoName.trim();
    if (!trimmed) {
      setListError('Logo name cannot be empty');
      return;
    }
    if (!uid) {
      return;
    }
    const db = getFirestore(firebase);
    setSavingLogoName(true);
    setListError('');
    try {
      await updateGymLogoName(db, uid, logoId, trimmed);
      setLogos((prev) =>
        prev.map((l) => (l.id === logoId ? { ...l, name: trimmed } : l)),
      );
      cancelEditLogoName();
    } catch (err: unknown) {
      setListError('Failed to update logo name');
      console.error(err);
    } finally {
      setSavingLogoName(false);
    }
  };

  const handleUploadClick = (): void => {
    setUploadError('');
    setDeleteError('');
    if (logos.length >= MAX_GYM_LOGOS) {
      setUploadError(`Maximum of ${MAX_GYM_LOGOS} logos reached. Delete one to upload more.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!e.target.files) {
      return;
    }
    // Reset input value so re-selecting the same file triggers onChange again.
    e.target.value = '';

    if (!file) {
      return;
    }

    if (!(GYM_LOGO_ALLOWED_TYPES as readonly string[]).includes(file.type)) {
      setUploadError('Only JPG and PNG files are allowed.');
      return;
    }
    if (file.size > GYM_LOGO_MAX_BYTES) {
      setUploadError('File is too large. Maximum size is 1 MB.');
      return;
    }

    // Revoke any existing preview before creating a new one.
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
    }
    const url = URL.createObjectURL(file);
    previewBlobRef.current = url;

    const defaultName = file.name.replace(/\.[^.]+$/, '');
    setEditingLogoId(null);
    setDraftLogoName('');
    setPendingFile(file);
    setPendingName(defaultName);
    setPreviewUrl(url);
    setUploadError('');
  };

  const handleUploadConfirm = async (): Promise<void> => {
    if (!pendingFile || !uid) {
      return;
    }
    const trimmedName = pendingName.trim();
    if (!trimmedName) {
      setUploadError('Please enter a name for this logo.');
      return;
    }
    if (logos.length >= MAX_GYM_LOGOS) {
      setUploadError(`Maximum of ${MAX_GYM_LOGOS} logos reached.`);
      return;
    }
    const db = getFirestore(firebase);
    setUploading(true);
    setUploadError('');
    try {
      const newLogo = await uploadGymLogo(firebase, db, uid, pendingFile, trimmedName);
      setLogos((prev) => [newLogo, ...prev]);
      clearPendingUpload();
    } catch (err: unknown) {
      setUploadError('Upload failed. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deletePending || !uid) {
      return;
    }
    const db = getFirestore(firebase);
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteGymLogo(firebase, db, uid, deletePending);
      setLogos((prev) => prev.filter((l) => l.id !== deletePending.id));
      setDeletePending(null);
    } catch (err: unknown) {
      setDeleteError('Failed to delete logo. Please try again.');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="gym-logos">
      <div className="gym-logos-header">
        <h2 className="gym-logos-title">Gym Logos</h2>
        <Button
          type="button"
          variant="primary"
          onClick={handleUploadClick}
          disabled={uploading || pendingFile != null}
        >
          Upload Logo
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          className="gym-logos-file-input"
          onChange={handleFileSelect}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {listError ? <div className="gym-logos-error">{listError}</div> : null}
      {deleteError ? <div className="gym-logos-error">{deleteError}</div> : null}

      {pendingFile ? (
        <Card className="gym-logos-upload-panel">
          <div className="gym-logos-upload-inner">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="gym-logos-upload-preview"
              />
            ) : null}
            <div className="gym-logos-upload-form">
              <span className="gym-logos-upload-field-label">Logo name</span>
              <div className="gym-logos-upload-input-row">
                <InlineInputWithIntegratedCheckmark
                  value={pendingName}
                  onChange={setPendingName}
                  onSave={() => void handleUploadConfirm()}
                  onCancel={clearPendingUpload}
                  saving={uploading}
                  maxLength={80}
                  placeholder="e.g. Cheer Force"
                  inputAriaLabel="Logo name"
                  commitAriaLabel={uploading ? 'Uploading' : 'Upload logo'}
                  emphasize={false}
                />
                <IconButton
                  aria-label="Discard upload"
                  title="Discard upload"
                  onClick={clearPendingUpload}
                  disabled={uploading}
                >
                  <TrashIcon />
                </IconButton>
              </div>
              {uploadError ? (
                <div className="gym-logos-error">{uploadError}</div>
              ) : null}
            </div>
          </div>
        </Card>
      ) : uploadError ? (
        <div className="gym-logos-error">{uploadError}</div>
      ) : null}

      {loading ? (
        <p className="gym-logos-status">Loading…</p>
      ) : !pendingFile && logos.length === 0 ? (
        <p className="gym-logos-status">No logos uploaded yet.</p>
      ) : null}

      <div className="gym-logos-list">
        {logos.map((logo) => (
          <Card key={logo.id} className="gym-logos-card">
            <div className="gym-logos-card-inner">
              <img
                src={logo.imgUrl}
                alt={logo.name}
                className="gym-logos-thumbnail"
                loading="lazy"
                decoding="async"
              />
              <div className="gym-logos-info">
                <div className="gym-logos-name-row">
                  {editingLogoId === logo.id ? (
                    <InlineInputWithIntegratedCheckmark
                      value={draftLogoName}
                      onChange={setDraftLogoName}
                      onSave={() => void saveEditLogoName(logo.id)}
                      onCancel={cancelEditLogoName}
                      saving={savingLogoName}
                      maxLength={80}
                      placeholder="Logo name"
                      inputAriaLabel="Logo name"
                      commitAriaLabel="Save logo name"
                      emphasize={false}
                    />
                  ) : (
                    <div className="gym-logos-name-with-pencil">
                      <span className="gym-logos-name">
                        {logo.name.trim() || 'Untitled logo'}
                      </span>
                      <IconButton
                        aria-label={`Edit ${logo.name}`}
                        onClick={() => beginEditLogoName(logo)}
                        disabled={deleting || uploading}
                      >
                        <PencilIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
                <span className="gym-logos-date">
                  {logo.created.toDate().toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <IconButton
                aria-label={`Delete ${logo.name}`}
                onClick={() => {
                  setDeleteError('');
                  setDeletePending(logo);
                }}
                disabled={deleting}
              >
                <TrashIcon />
              </IconButton>
            </div>
          </Card>
        ))}
      </div>

      <ConfirmDialog
        open={deletePending != null}
        title="Delete logo?"
        message={
          deletePending
            ? `Permanently delete "${deletePending.name}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeletePending(null)}
      />
    </div>
  );
};

export default GymLogos;
